<script setup>
import { composeEventDateTime } from '#shared/utilities/schedule/event-options';
import { getConferenceTimezone } from '#shared/utilities/schedule';
import { conflictKindFromId } from '#shared/utilities/schedule/double-book-copy';
import {
    modal_button_filled_2026,
    modal_button_outline_2026,
    form_field_error_border_2026,
    form_field_error_text_2026,
    form_error_text_2026,
} from '#shared/utilities/ui';

const props = defineProps({
    mode: { type: String, default: 'create' },   // 'create' | 'edit'
    event: { type: Object, default: null },       // raw user_event row on edit
});
const open = defineModel('open', { default: true });
const emit = defineEmits(['close']);

const { createEvent, updateEvent, deleteEvent, loadOptions, events, loadEvents } = useMyEvents();
// Reaching the OTHER event's editor from in here — see openConflict. A dialog
// can't inject the agenda's openMyEvent (it mounts at the app root, outside the
// agenda's subtree), so it goes through the module-scoped bridge.
const { openMyEventFromDialog } = useMyEventOpener();
const tz = getConferenceTimezone(2026);

const view = ref('form');                 // form | conflict | exitConfirm | deleteConfirm
const busy = ref(false);
const errorMsg = ref('');
const conflictTitle = ref('');
const conflictId = ref(null);
const days = ref([]);
const times = ref([]);

// Split an ISO datetime back into { day:'YYYY-MM-DD', hhmm:'HH:mm' } in ET so the
// edit form re-selects the right dropdown values.
const splitEt = (iso) => {
    if (!iso) return { day: '', hhmm: '' };
    const d = new Date(iso);
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(d).reduce((a, p) => (a[p.type] = p.value, a), {});
    return { day: `${parts.year}-${parts.month}-${parts.day}`, hhmm: `${parts.hour === '24' ? '00' : parts.hour}:${parts.minute}` };
};

const initial = () => {
    if (props.mode === 'edit' && props.event) {
        const s = splitEt(props.event.startTime);
        const e = splitEt(props.event.endTime);
        return { title: props.event.title || '', description: props.event.description || '', day: s.day, start: s.hhmm, end: e.hhmm };
    }
    return { title: '', description: '', day: '', start: '', end: '' };
};

const form = reactive(initial());
const snapshot = ref(JSON.stringify(form));
const isDirty = computed(() => JSON.stringify(form) !== snapshot.value);

onMounted(async () => {
    try {
        const opts = await loadOptions();
        days.value = opts.days || [];
        times.value = opts.times || [];
    } catch { /* options unavailable — dropdowns render empty */ }
});

const buildBody = () => ({
    title: form.title.trim(),
    description: form.description.trim() || null,
    day: form.day,
    startTime: form.day && form.start ? composeEventDateTime(form.day, form.start, tz) : '',
    endTime: form.day && form.end ? composeEventDateTime(form.day, form.end, tz) : '',
});

// Per-field errors (UNB). Every required field reports its own message beneath
// itself; `errorMsg` is now only for form-level failures (network / server),
// which still render once below the fields.
const fieldErrors = reactive({ title: '', day: '', start: '', end: '' });
const REQUIRED = 'This field is required';

// Returns true when the form has errors, so submit can bail.
const clientValidate = () => {
    for (const key of Object.keys(fieldErrors)) fieldErrors[key] = '';

    if (!form.title.trim()) fieldErrors.title = REQUIRED;
    if (!form.day) fieldErrors.day = REQUIRED;
    if (!form.start) fieldErrors.start = REQUIRED;
    if (!form.end) fieldErrors.end = REQUIRED;

    // Ordering can only be judged once the day and both times are present —
    // composeEventDateTime needs all three, and a missing one already has its
    // own "required" message.
    if (form.day && !fieldErrors.start && !fieldErrors.end) {
        const body = buildBody();
        // Times are selectable in 15-minute steps (event-options.js), so the next
        // valid option after the start IS +15 minutes — the copy can state the
        // real constraint (Figma 4683-93020) instead of a vaguer "after".
        if (new Date(body.endTime).getTime() <= new Date(body.startTime).getTime()) {
            fieldErrors.end = 'The end time must be at least 15 minutes after the start time';
        }
    }

    return Object.values(fieldErrors).some(Boolean);
};

const submit = async () => {
    errorMsg.value = '';
    if (clientValidate()) return;
    busy.value = true;
    try {
        const body = buildBody();
        const res = props.mode === 'edit'
            ? await updateEvent(props.event.id, body)
            : await createEvent(body);
        if (res?.conflict) { conflictTitle.value = res.conflict.title || ''; conflictId.value = res.conflict.unique_id || null; view.value = 'conflict'; return; }
        finish();
    } catch { errorMsg.value = 'Something went wrong. Please try again.'; }
    finally { busy.value = false; }
};

const doDelete = async () => {
    busy.value = true;
    try { await deleteEvent(props.event.id); finish(); }
    catch { errorMsg.value = 'Could not delete the event. Please try again.'; view.value = 'form'; }
    finally { busy.value = false; }
};

const finish = () => { emit('close', { changed: true }); };

// The ✕ / Cancel path: confirm only when the form is dirty.
const attemptClose = () => {
    if (isDirty.value) { view.value = 'exitConfirm'; }
    else { open.value = false; }
};

// Veto hook passed to ModalShell: intercept ✕ / ESC / backdrop. Only the form
// view with unsaved edits needs confirmation; every other view closes normally.
const handleBeforeClose = () => {
    if (view.value === 'form' && isDirty.value) { view.value = 'exitConfirm'; return false; }
    return true;
};

// Following the conflicting item, whichever kind it is. A catalog session opens
// its detail tray; a CUSTOM EVENT opens that event's own editor.
//
// The custom-event half is instance 3 of this ticket — creating an event that
// collides with an existing one — and it was previously left as plain text here,
// on the reasoning that opening another event's editor would discard the form in
// progress. That was wrong twice over: it's the exact case QA asked to link, and
// concealing (rather than closing) keeps this dialog mounted, so the form values
// and the conflict view survive untouched.
//
// The modal is CONCEALED while the tray or the other editor is up — the same fix
// find-a-session.vue uses — and comes back intact once either closes. It is never
// closed first: doing that threw away everything the user had typed (UNB).
//
// The server sends only { unique_id, title } for the conflict, but the editor is
// populated from the raw row, so the id is resolved back against myEvents first.
const editorOpen = ref(false);

const openConflict = async (id) => {
    if (conflictKindFromId(id) !== 'event') {
        await navigateTo(`/sessions/${id}`);
        return;
    }
    const eventId = id.slice('event:'.length);
    let raw = (events.value || []).find((e) => String(e.id) === eventId);
    // No row means the list is stale (added on another tab); reload once rather
    // than dead-clicking.
    if (!raw) {
        await loadEvents();
        raw = (events.value || []).find((e) => String(e.id) === eventId);
    }
    if (!raw) return;

    editorOpen.value = true;
    try { await openMyEventFromDialog(raw); }
    catch { /* the editor reports its own failures; just don't strand us hidden */ }
    finally {
        editorOpen.value = false;
        // They went to remove or reschedule the other event, so the collision may
        // be gone. Land back on the form rather than on a conflict notice about a
        // state that no longer exists.
        view.value = 'form';
    }
};

// The global modal middleware rewrites /sessions/{id} to `?drawer=`, which
// app.vue renders as the session drawer on top of this modal. Any drawer counts:
// a session is the only one reachable from here, and a modal is never the right
// thing to leave showing under one.
const route = useRoute();
const drawerOpen = computed(() => !!route.query.drawer);
// Raised while the conflict modal is up, so this dialog goes invisible rather
// than closing — see openConflicts below.
const conflictsOpen = ref(false);
const concealed = computed(() => drawerOpen.value || editorOpen.value || conflictsOpen.value);

const headline = computed(() => (props.mode === 'edit' ? 'View/Edit My Event' : 'Create My Event'));

// ── Conflict notice (Figma 4753:108224) ─────────────────────────────────────
// Edit only: a create form has no event yet, so nothing can clash with it.
// A custom event's conflict key is `event:<id>` — the same prefix
// normalize-event.js gives it, and what the conflicts endpoint returns.
const scheduleConflicts = useScheduleConflicts();
onMounted(() => { scheduleConflicts.ensureConflictIds(); });

const conflictKey = computed(() => (props.event?.id ? `event:${props.event.id}` : null));
const isConflicted = computed(() => (
    props.mode === 'edit' && !!conflictKey.value && scheduleConflicts.isConflicted(conflictKey.value)
));
const conflictPartnerCount = computed(() => (
    conflictKey.value ? scheduleConflicts.conflictPartners(conflictKey.value).length : 0
));

// Same rule as the drawer's note: the resolution sentence is only true when there
// is exactly ONE other thing in the slot, so the multiple-clash comp drops it.
const CONFLICT_NOTE = 'This event is in the same time slot as other items on My Schedule and requires action.';
const CONFLICT_NOTE_RESOLVES = 'Deleting it from My Schedule or changing the date/time to fill an open time slot on My Schedule will resolve the issue.';
const conflictNote = computed(() => (
    conflictPartnerCount.value === 1
        ? `${CONFLICT_NOTE} ${CONFLICT_NOTE_RESOLVES}`
        : CONFLICT_NOTE
));

// CONCEAL this dialog while the conflict modal is up, rather than closing it.
// Closing was a one-way door: dismissing the conflict list dropped the user back
// on the page with their half-finished event gone, when the whole reason to read
// that list is to come back and edit the event out of the clash (Nick, Asana
// 1217708959827752).
//
// The original worry here was two competing focus traps. Concealing sets
// `visibility: hidden` on the overlay, which takes everything inside it out of
// the tab order, so there is only ever one trap — the same reason Find a Session
// can sit under a session drawer.
//
// Pressing "Take Me to Fix This" is the one case that still closes: that
// navigates to the agenda, and this dialog is mounted at the app root, so
// revealing it again would leave the event editor floating over a different page.
const openConflicts = async () => {
    let navigatedToFix = false;
    conflictsOpen.value = true;
    try {
        await scheduleConflicts.maybeShow(async () => {
            navigatedToFix = true;
            await navigateTo({
                path: '/agenda',
                query: { tab: 'myschedule', view: 'calendar', conflicts: 1 },
            });
        }, { force: true });
    } finally {
        conflictsOpen.value = false;
    }
    if (navigatedToFix) open.value = false;
};

// Figma field spec: 65px-tall pill inputs (py-21 px-26), 112px rounded-[10px]
// description textarea, 290px selects with a chevron, 15px/0.3px light text.
// The border COLOUR is applied separately, via fieldBorder() below — listing
// both colours in one class string wouldn't work, since two border-color
// utilities of equal specificity resolve by stylesheet order, not by the order
// they appear in the attribute. (Same ternary pattern as schedule-prefs.vue.)
const inputBase = 'w-full bg-white border rounded-[50px] py-[21px] px-[26px] text-[15px] leading-[23px] tracking-[0.3px] font-hubspot-sans font-light text-(--magenta) placeholder:text-(--dark-pink) focus:outline-none';
const textareaBase = 'w-full bg-white border rounded-[10px] py-[25px] px-[26px] min-h-[112px] text-[15px] leading-[23px] tracking-[0.3px] font-hubspot-sans font-light text-(--magenta) placeholder:text-(--dark-pink) resize-y focus:outline-none';
const selectBase = 'w-full bg-white border rounded-[50px] py-[21px] pl-[26px] pr-[45px] text-[15px] leading-[23px] tracking-[0.3px] font-hubspot-sans font-light text-(--magenta) appearance-none cursor-pointer focus:outline-none';
const fieldBorder = (hasError) => (hasError ? form_field_error_border_2026 : 'border-(--dark-pink)');
const labelCls = 'w-full text-left font-hubspot-sans font-light text-[13px] leading-[23px] tracking-[0.26px] text-(--magenta)';
</script>

<template>
    <FragmentsModalShell
        v-model:open="open"
        :headline="''"
        title="My event"
        :dismissable="false"
        :concealed="concealed"
        :before-close="handleBeforeClose"
        @close="emit('close')"
    >
        <!-- BACK affordance on the confirm sub-views -->
        <template v-if="view !== 'form'" #top-left>
            <button type="button" class="flex items-center gap-[11px] font-hubspot-sans font-medium text-[12px] tracking-[0.84px] uppercase text-[#7d4566] cursor-pointer" @click="view = 'form'">
                <svg width="7" height="8" viewBox="0 0 7 8" fill="none" aria-hidden="true"><path d="M5 1L1.5 4L5 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /></svg>
                Back
            </button>
        </template>

        <!-- FORM — content is a fixed ~681px block centered in the ~900px modal
             (≈110px gutters), matching Figma; the shell inner centers it. The
             pb tops up the shell's ~24px body padding to Figma's ~70px bottom
             gutter below the action buttons. -->
        <form v-if="view === 'form'" class="flex flex-col w-full max-w-[681px] text-left pb-[46px]" @submit.prevent="submit">
            <h2 class="m-0 w-full font-hubspot-serif font-normal text-[36px] leading-[1.2] tracking-normal text-(--magenta)">{{ headline }}</h2>

            <!-- Create-mode intro (UNB-614): explains what a My Event is for. Edit
                 mode omits it — the user already knows. -->
            <p v-if="mode === 'create'" class="m-0 mt-[15px] w-full font-hubspot-sans font-light text-[15px] leading-[23px] tracking-[0.3px] text-(--magenta)">
                Keep everything in one place. Add your own meetings, travel, breaks, or personal plans to your UNBOUND schedule.
            </p>

            <!-- Figma 4753:108224 — sits in the modal's 30px rhythm between the
                 headline and the form. -->
            <div
                v-if="isConflicted"
                data-test="my-event-conflict-note"
                class="mt-[30px] w-full rounded-[10px] bg-[#980000] px-[30px] py-[24px]"
            >
                <p class="m-0 w-full font-hubspot-sans font-light text-[16px] leading-[26px] tracking-[0.32px] sm:text-[14px] sm:leading-[1.5] sm:tracking-[0.28px] text-white">
                    {{ conflictNote }}
                    <button
                        type="button"
                        data-test="my-event-conflict-learn-more"
                        class="cursor-pointer border-none bg-transparent p-0 font-[inherit] text-[inherit] leading-[inherit] tracking-[inherit] text-white
                            align-baseline
                            underline decoration-dotted decoration-white decoration-[8.5%] underline-offset-[3px]
                            transition-[text-decoration] duration-200 hover:no-underline"
                        @click.stop.prevent="openConflicts"
                    >Learn More</button>
                </p>
            </div>

            <div class="flex flex-col gap-[20px] w-full" :class="isConflicted ? 'mt-[30px]' : 'mt-[25px]'">
                <div class="flex flex-col gap-[9px]">
                    <label :class="labelCls">Event Name:<span class="text-(--magenta)">*</span></label>
                    <div class="flex flex-col gap-[10px]">
                        <!-- 60-char cap (UNB): maxlength stops the entry rather than
                             erroring after the fact. -->
                        <input v-model="form.title" type="text" maxlength="60" :class="[inputBase, fieldBorder(!!fieldErrors.title)]" placeholder="Enter a name for My Event" />
                        <p v-if="fieldErrors.title" data-test="event-error-title" :class="form_field_error_text_2026">{{ fieldErrors.title }}</p>
                    </div>
                </div>

                <div class="flex flex-col gap-[9px]">
                    <label :class="labelCls">Event Description/Notes (Optional):</label>
                    <textarea v-model="form.description" :class="[textareaBase, fieldBorder(false)]" placeholder="Enter a description" />
                </div>

                <div class="flex flex-col gap-[9px]">
                    <label :class="labelCls">Day:<span class="text-(--magenta)">*</span></label>
                    <div class="flex flex-col gap-[10px] w-full sm:w-[290px]">
                        <div class="relative w-full">
                            <select v-model="form.day" :class="[selectBase, fieldBorder(!!fieldErrors.day)]">
                                <option value="" disabled>Select Day</option>
                                <option v-for="d in days" :key="d.value" :value="d.value">{{ d.label }}</option>
                            </select>
                            <svg class="pointer-events-none absolute right-[26px] top-1/2 -translate-y-1/2 w-[10px] h-[6px] text-(--magenta)" viewBox="0 0 9 5" fill="none" aria-hidden="true"><path d="M1 1L4.5 4L8 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /></svg>
                        </div>
                        <p v-if="fieldErrors.day" data-test="event-error-day" :class="form_field_error_text_2026">{{ fieldErrors.day }}</p>
                    </div>
                </div>

                <!-- Top-aligned (Figma 4683-92320): an error message under either
                     column grows it downward, so bottom alignment would drag the
                     "To:" divider out of line with the inputs. -->
                <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-[16px] sm:gap-0 w-full">
                    <div class="flex flex-col gap-[9px] w-full sm:w-[290px]">
                        <label :class="labelCls">Start Time (ET):<span class="text-(--magenta)">*</span></label>
                        <div class="relative w-full">
                            <select v-model="form.start" :class="[selectBase, fieldBorder(!!fieldErrors.start)]">
                                <option value="" disabled>Select Time</option>
                                <option v-for="t in times" :key="'s' + t.value" :value="t.value">{{ t.label }}</option>
                            </select>
                            <svg class="pointer-events-none absolute right-[26px] top-1/2 -translate-y-1/2 w-[10px] h-[6px] text-(--magenta)" viewBox="0 0 9 5" fill="none" aria-hidden="true"><path d="M1 1L4.5 4L8 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /></svg>
                        </div>
                        <p v-if="fieldErrors.start" data-test="event-error-start" :class="form_field_error_text_2026">{{ fieldErrors.start }}</p>
                    </div>
                    <!-- Sits 30.4px down from the row top so it centres against the
                         inputs, which start below their 23px labels + 9px gap. -->
                    <span class="hidden sm:block font-hubspot-sans font-medium text-[13px] leading-none tracking-[1.04px] uppercase text-(--magenta) mt-[30.4px]">To:</span>
                    <div class="flex flex-col gap-[9px] w-full sm:w-[290px]">
                        <label :class="labelCls">End Time (ET):<span class="text-(--magenta)">*</span></label>
                        <div class="relative w-full">
                            <select v-model="form.end" :class="[selectBase, fieldBorder(!!fieldErrors.end)]">
                                <option value="" disabled>Select Time</option>
                                <option v-for="t in times" :key="'e' + t.value" :value="t.value">{{ t.label }}</option>
                            </select>
                            <svg class="pointer-events-none absolute right-[26px] top-1/2 -translate-y-1/2 w-[10px] h-[6px] text-(--magenta)" viewBox="0 0 9 5" fill="none" aria-hidden="true"><path d="M1 1L4.5 4L8 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /></svg>
                        </div>
                        <p v-if="fieldErrors.end" data-test="event-error-end" :class="form_field_error_text_2026">{{ fieldErrors.end }}</p>
                    </div>
                </div>
            </div>

            <p v-if="errorMsg" data-test="event-error-form" :class="form_error_text_2026">{{ errorMsg }}</p>

            <div class="flex flex-col items-start gap-[20px] mt-[45px]">
                <div class="flex flex-row flex-wrap items-center gap-[16px]">
                    <!-- Create mode's "Add My Event" uses the outline treatment
                         (magenta text + 1px stroke, fills on hover) per UNB — edit
                         mode's "Save Changes" keeps the filled primary. -->
                    <!-- Edit mode's Save Changes stays disabled until something
                         actually changes, matching schedule preferences (UNB).
                         Create mode never disables — the user needs to be able to
                         submit an empty form and see which fields are required. -->
                    <button type="submit" :disabled="busy || (mode === 'edit' && !isDirty)" data-test="event-submit" :class="[mode === 'edit' ? modal_button_filled_2026 : modal_button_outline_2026, 'disabled:opacity-60', mode === 'edit' && !isDirty ? 'disabled:cursor-not-allowed' : '']">
                        {{ mode === 'edit' ? 'Save Changes' : 'Add My Event' }}
                    </button>
                    <button v-if="mode === 'edit'" type="button" :class="modal_button_outline_2026" @click="view = 'deleteConfirm'">
                        Delete Event
                    </button>
                </div>
                <button type="button" class="font-hubspot-sans font-light text-[14px] text-[#7d4566] underline decoration-dotted underline-offset-[3px] cursor-pointer transition-colors duration-200 hover:text-(--purple) hover:no-underline" @click="attemptClose">Cancel</button>
            </div>
        </form>

        <!-- CONFLICT -->
        <FragmentsDialogsConfirmPanel
            v-else-if="view === 'conflict'"
            headline="Oops, looks like you’re trying to double book!"
            primary-label="Edit My Event"
            secondary-label="Close"
            primary-variant="outline"
            @primary="view = 'form'"
            @secondary="open = false"
        >
            <template #body>
                <FragmentsDoubleBookMessage
                    new-kind="event"
                    :new-title="form.title"
                    :conflict-title="conflictTitle"
                    :conflict-id="conflictId"
                    link-events
                    @navigate="openConflict"
                />
            </template>
        </FragmentsDialogsConfirmPanel>

        <!-- EXIT WITHOUT SAVING -->
        <FragmentsDialogsConfirmPanel
            v-else-if="view === 'exitConfirm'"
            headline="Are you sure you want to close without saving your changes?"
            body="All changes will be discarded when you close without saving."
            primary-label="Save Changes"
            secondary-label="Close Without Saving"
            @primary="() => { view = 'form'; submit(); }"
            @secondary="open = false"
        />

        <!-- DELETE -->
        <FragmentsDialogsConfirmPanel
            v-else-if="view === 'deleteConfirm'"
            :headline="`Are you sure you want to delete the event “${form.title.trim()}”?`"
            body="This action can’t be undone."
            primary-label="Cancel"
            secondary-label="Delete Event"
            @primary="view = 'form'"
            @secondary="doDelete"
        />
    </FragmentsModalShell>
</template>
